import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCardModule } from '@angular/material/card';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatMenuModule } from '@angular/material/menu';
import { MatLuxonDateModule } from '@angular/material-luxon-adapter';
import { NgApexchartsModule } from 'ng-apexcharts';
import { ApiService } from 'shared/services/api.service';
import { SnackbarService } from 'shared/services/snackbar.service';
import { APIResponse } from 'fakturnia-shared';
import { Subscription } from 'rxjs';
import { DateTime } from 'luxon';

const SOURCES_ORDER = ['WEB', 'IOS', 'ANDROID', 'MOBILE', 'UNKNOWN'] as const;

interface RegisterSourceStat {
  source: string;
  count: number;
  recentUsers: { _id: string; emailAddress: string; createdAt: string }[];
}

interface OverviewStats {
  totalUsers: number;
  totalDocuments: number;
  todayRegistrations: number;
  todayDocuments: number;
  thisMonthRegistrations: number;
  thisMonthDocuments: number;
  thisMonthLocalDocuments: number;
  thisMonthKsefDocuments: number;
  registrationsChange: number;
  documentsChange: number;
  lastMonthRegistrations: number;
  lastMonthDocuments: number;
  activeRegularUsers: number;
  last30DaysRevenue: number;
  last30DaysOrders: number;
  last30DaysActiveUsers: number;
  ksefConnections: number;
  registerSourceStats: RegisterSourceStat[];
  ksefSync?: {
    syncFailed24h: number;
    syncFailed7d: number;
    authFailed7d: number;
    totalSyncSuccess24h: number;
    totalSyncFailed24h: number;
    connectionsWithErrors: number;
    lastCron: {
      timestamp: string;
      successCount: number;
      failedCount: number;
      usersProcessed: number;
      duration: number;
      failedUsers: { userId: string; userLabel: string; error: string }[];
    } | null;
  };
}

@Component({
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatButtonToggleModule,
    MatCardModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatTooltipModule,
    MatMenuModule,
    MatLuxonDateModule,
    NgApexchartsModule
  ],
  selector: 'app-admin-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss']
})
export class AdminDashboardComponent implements OnInit, OnDestroy {
  private _subscriptions: Subscription[] = [];

  // Loading states
  isLoadingOverview = true;
  isLoadingDailyChart = true;
  isLoadingMonthlyChart = true;
  isLoadingRevenueChart = true;
  isLoadingRegisterSourceChart = true;

  // Overview stats
  overviewStats: OverviewStats | null = null;

  // Chart configs
  dailyChartConfig: any = null;
  monthlyChartConfig: any = null;
  revenueChartConfig: any = null;

  // Revenue summary
  revenueSummary: { totalRevenue: number; totalOrders: number; averageOrderValue: number } | null = null;

  // Register source chart
  registerSourceChartConfig: any = null;
  registerSourceTimeChartConfig: any = null;

  // Backfill register source
  isBackfillingRegisterSource = false;

  // Monthly chart range selector
  selectedRange: 'thisYear' | 'last12Months' | 'last3Years' | string = 'last12Months';

  // Revenue chart range selector (oddzielne od monthly)
  revenueSelectedRange: 'thisYear' | 'last12Months' | 'last3Years' | string = 'last12Months';
  revenueStartDate: DateTime = DateTime.now().minus({ months: 11 }).startOf('month');
  revenueEndDate: DateTime = DateTime.now().endOf('month');
  revenueGroupingMode: 'monthly' | 'yearly' = 'monthly';
  revenueManualGroupingMode: boolean = false;

  // Date pickers for custom range
  startDate: DateTime = DateTime.now().minus({ months: 11 }).startOf('month');
  endDate: DateTime = DateTime.now().endOf('month');

  // Grouping mode
  groupingMode: 'monthly' | 'yearly' = 'monthly';
  manualGroupingMode: boolean = false; // Czy użytkownik manualnie wybrał tryb

  // Lista dostępnych lat (od 2022 do aktualnego roku)
  availableYears: number[] = [];

  constructor(
    private _apiService: ApiService,
    private _snackbarService: SnackbarService
  ) {
    // Generuj listę lat od 2022 do aktualnego roku
    const currentYear = DateTime.now().year;
    for (let year = 2022; year <= currentYear; year++) {
      this.availableYears.push(year);
    }
  }

  ngOnInit(): void {
    this._loadOverviewStats();
    this._loadDailyChart();
    this._loadMonthlyChart();
    this._loadRegisterSourceChart();
    this._loadRevenueChart();
  }

  ngOnDestroy(): void {
    this._subscriptions.forEach(sub => sub.unsubscribe());
  }

  onRangeChange(range: 'thisYear' | 'last12Months' | 'last3Years' | string): void {
    this.selectedRange = range;
    
    // Sprawdź czy to wybór roku (np. "2023")
    if (!isNaN(Number(range))) {
      this._setYearRange(Number(range));
    } else {
      this._updateDatesForRange(range as any);
    }
    
    if (!this.manualGroupingMode) {
      this._updateGroupingMode();
    }
    this._loadMonthlyChart();
    this._loadRegisterSourceChart();
  }

  onCustomDateChange(): void {
    this.selectedRange = 'custom' as any;
    if (!this.manualGroupingMode) {
      this._updateGroupingMode();
    }
    this._loadMonthlyChart();
    this._loadRegisterSourceChart();
  }

  onGroupingModeToggle(): void {
    this.manualGroupingMode = true;
    this.groupingMode = this.groupingMode === 'monthly' ? 'yearly' : 'monthly';
    this._loadMonthlyChart();
    this._loadRegisterSourceChart();
  }

  isYearSelected(): boolean {
    return !isNaN(Number(this.selectedRange));
  }

  private _setYearRange(year: number): void {
    this.startDate = DateTime.fromObject({ year, month: 1, day: 1 }).startOf('day');
    this.endDate = DateTime.fromObject({ year, month: 12, day: 31 }).endOf('day');
  }

  private _updateDatesForRange(range: 'thisYear' | 'last12Months' | 'last3Years'): void {
    const now = DateTime.now();
    
    switch (range) {
      case 'thisYear':
        this.startDate = now.startOf('year');
        this.endDate = now.endOf('year');
        break;
      case 'last12Months':
        this.startDate = now.minus({ months: 11 }).startOf('month');
        this.endDate = now.endOf('month');
        break;
      case 'last3Years':
        this.startDate = now.minus({ months: 35 }).startOf('month');
        this.endDate = now.endOf('month');
        break;
    }
  }

  private _updateGroupingMode(): void {
    // Oblicz różnicę w miesiącach między datami
    const diff = this.endDate.diff(this.startDate, 'months').months;
    
    // Jeśli zakres > 12 miesięcy, użyj grupowania rocznego
    this.groupingMode = diff > 12 ? 'yearly' : 'monthly';
    this.manualGroupingMode = false; // Reset manualnego trybu
  }

  private _loadOverviewStats(): void {
    this.isLoadingOverview = true;
    const sub = this._apiService.getAdminOverviewStats().subscribe({
      next: (response: APIResponse) => {
        if (response.success) {
          this.overviewStats = response.data;
          this._buildRegisterSourceChart();
        }
        this.isLoadingOverview = false;
      },
      error: () => {
        this.isLoadingOverview = false;
      }
    });
    this._subscriptions.push(sub);
  }

  private _loadDailyChart(): void {
    this.isLoadingDailyChart = true;
    const sub = this._apiService.getAdminDailyChart().subscribe({
      next: (response: APIResponse) => {
        if (response.success) {
          this._buildDailyChart(response.data);
        }
        this.isLoadingDailyChart = false;
      },
      error: () => {
        this.isLoadingDailyChart = false;
      }
    });
    this._subscriptions.push(sub);
  }

  private _loadMonthlyChart(): void {
    this.isLoadingMonthlyChart = true;
    
    if (this.groupingMode === 'yearly') {
      // Użyj yearly chart
      const sub = this._apiService.getAdminYearlyChart(this.startDate.toJSDate(), this.endDate.toJSDate()).subscribe({
        next: (response: APIResponse) => {
          if (response.success) {
            this._buildYearlyChart(response.data);
          }
          this.isLoadingMonthlyChart = false;
        },
        error: () => {
          this.isLoadingMonthlyChart = false;
        }
      });
      this._subscriptions.push(sub);
    } else {
      // Użyj monthly chart
      const range = this.selectedRange as any;
      
      // Jeśli selectedRange to rok (string liczbowy) lub 'custom', użyj dat
      const isYearSelection = !isNaN(Number(range));
      const isCustomRange = range === 'custom' || isYearSelection;
      
      const startDate = isCustomRange ? this.startDate.toJSDate() : undefined;
      const endDate = isCustomRange ? this.endDate.toJSDate() : undefined;
      const effectiveRange = isCustomRange ? 'custom' : range;
      
      const sub = this._apiService.getAdminMonthlyChart(effectiveRange, startDate, endDate).subscribe({
        next: (response: APIResponse) => {
          if (response.success) {
            this._buildMonthlyChart(response.data);
          }
          this.isLoadingMonthlyChart = false;
        },
        error: () => {
          this.isLoadingMonthlyChart = false;
        }
      });
      this._subscriptions.push(sub);
    }
  }

  private _buildDailyChart(data: any[]): void {
    const newUsersData = data.map(d => ({ x: DateTime.fromISO(d.date).toMillis(), y: d.userCount }));
    const localDocumentsData = data.map(d => ({ x: DateTime.fromISO(d.date).toMillis(), y: d.localDocumentCount }));
    const ksefDocumentsData = data.map(d => ({ x: DateTime.fromISO(d.date).toMillis(), y: d.ksefDocumentCount }));
    const activeUsersData = data.map(d => ({ x: DateTime.fromISO(d.date).toMillis(), y: d.distinctUserCount }));

    this.dailyChartConfig = {
      series: [
        { name: 'Nowi użytkownicy', data: newUsersData },
        { name: 'Dokumenty (local)', data: localDocumentsData },
        { name: 'Dokumenty (KSeF)', data: ksefDocumentsData },
        { name: 'Aktywni użytkownicy', data: activeUsersData }
      ],
      chart: {
        type: 'area',
        height: 350,
        toolbar: { show: false },
        zoom: { enabled: false }
      },
      colors: ['#3B82F6', '#10B981', '#8B5CF6', '#F59E0B'],
      fill: {
        type: 'gradient',
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.7,
          opacityTo: 0.2,
          stops: [0, 90, 100]
        }
      },
      dataLabels: { enabled: false },
      stroke: { curve: 'smooth', width: 2 },
      xaxis: {
        type: 'datetime',
        labels: {
          format: 'dd MMM'
        }
      },
      yaxis: {
        labels: {
          formatter: (val) => Math.round(val).toString()
        }
      },
      tooltip: {
        x: { format: 'dd MMM yyyy' },
        y: {
          formatter: (val) => Math.round(val).toString()
        }
      },
      legend: {
        position: 'top',
        horizontalAlign: 'left'
      },
      grid: {
        borderColor: '#e7e7e7',
        strokeDashArray: 4
      }
    };
  }

  private _buildMonthlyChart(data: any[]): void {
    const categories = data.map(d => d.month);
    const newUsersData = data.map(d => d.userCount);
    const localDocumentsData = data.map(d => d.localDocumentCount ?? d.documentCount);
    const ksefDocumentsData = data.map(d => d.ksefDocumentCount ?? 0);
    const activeUsersData = data.map(d => d.distinctUserCount);

    this.monthlyChartConfig = {
      series: [
        { name: 'Nowi użytkownicy', data: newUsersData },
        { name: 'Dokumenty (lokalne)', data: localDocumentsData },
        { name: 'Dokumenty (KSeF)', data: ksefDocumentsData },
        { name: 'Aktywni użytkownicy', data: activeUsersData }
      ],
      chart: {
        type: 'line',
        height: 400,
        toolbar: { show: false },
        zoom: { enabled: false }
      },
      colors: ['#3B82F6', '#10B981', '#8B5CF6', '#F59E0B'],
      dataLabels: { enabled: false },
      stroke: {
        curve: 'smooth',
        width: 3
      },
      xaxis: {
        categories: categories,
        labels: {
          rotate: -45,
          rotateAlways: categories.length > 12
        }
      },
      yaxis: {
        labels: {
          formatter: (val) => Math.round(val).toString()
        }
      },
      tooltip: {
        y: {
          formatter: (val) => Math.round(val).toString()
        }
      },
      legend: {
        position: 'top',
        horizontalAlign: 'left'
      },
      grid: {
        borderColor: '#e7e7e7',
        strokeDashArray: 4
      },
      markers: {
        size: 4,
        hover: {
          size: 6
        }
      }
    };
  }

  private _buildYearlyChart(data: any[]): void {
    const categories = data.map(d => d.year);
    const newUsersData = data.map(d => d.userCount);
    const localDocumentsData = data.map(d => d.localDocumentCount ?? d.documentCount);
    const ksefDocumentsData = data.map(d => d.ksefDocumentCount ?? 0);
    const activeUsersData = data.map(d => d.distinctUserCount);

    this.monthlyChartConfig = {
      series: [
        { name: 'Nowi użytkownicy', data: newUsersData },
        { name: 'Dokumenty (lokalne)', data: localDocumentsData },
        { name: 'Dokumenty (KSeF)', data: ksefDocumentsData },
        { name: 'Aktywni użytkownicy', data: activeUsersData }
      ],
      chart: {
        type: 'bar',
        height: 400,
        toolbar: { show: false },
        stacked: false
      },
      colors: ['#3B82F6', '#10B981', '#8B5CF6', '#F59E0B'],
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: '70%',
          borderRadius: 8
        }
      },
      dataLabels: { 
        enabled: true,
        style: {
          fontSize: '12px',
          fontWeight: 'bold'
        }
      },
      stroke: {
        show: true,
        width: 2,
        colors: ['transparent']
      },
      xaxis: {
        categories: categories
      },
      yaxis: {
        labels: {
          formatter: (val) => Math.round(val).toString()
        }
      },
      fill: { opacity: 1 },
      tooltip: {
        y: {
          formatter: (val) => Math.round(val).toString()
        }
      },
      legend: {
        position: 'top',
        horizontalAlign: 'left'
      },
      grid: {
        borderColor: '#e7e7e7',
        strokeDashArray: 4
      }
    };
  }

  // ========== Revenue Chart Methods ==========

  onRevenueRangeChange(range: 'thisYear' | 'last12Months' | 'last3Years' | string): void {
    this.revenueSelectedRange = range;
    
    if (!isNaN(Number(range))) {
      this._setRevenueYearRange(Number(range));
    } else {
      this._updateRevenueDatesForRange(range as any);
    }
    
    if (!this.revenueManualGroupingMode) {
      this._updateRevenueGroupingMode();
    }
    this._loadRevenueChart();
  }

  onRevenueCustomDateChange(): void {
    this.revenueSelectedRange = 'custom' as any;
    if (!this.revenueManualGroupingMode) {
      this._updateRevenueGroupingMode();
    }
    this._loadRevenueChart();
  }

  onRevenueGroupingModeToggle(): void {
    this.revenueManualGroupingMode = true;
    this.revenueGroupingMode = this.revenueGroupingMode === 'monthly' ? 'yearly' : 'monthly';
    this._loadRevenueChart();
  }

  isRevenueYearSelected(): boolean {
    return !isNaN(Number(this.revenueSelectedRange));
  }

  private _setRevenueYearRange(year: number): void {
    this.revenueStartDate = DateTime.fromObject({ year, month: 1, day: 1 }).startOf('day');
    this.revenueEndDate = DateTime.fromObject({ year, month: 12, day: 31 }).endOf('day');
  }

  private _updateRevenueDatesForRange(range: 'thisYear' | 'last12Months' | 'last3Years'): void {
    const now = DateTime.now();
    
    switch (range) {
      case 'thisYear':
        this.revenueStartDate = now.startOf('year');
        this.revenueEndDate = now.endOf('year');
        break;
      case 'last12Months':
        this.revenueStartDate = now.minus({ months: 11 }).startOf('month');
        this.revenueEndDate = now.endOf('month');
        break;
      case 'last3Years':
        this.revenueStartDate = now.minus({ months: 35 }).startOf('month');
        this.revenueEndDate = now.endOf('month');
        break;
    }
  }

  private _updateRevenueGroupingMode(): void {
    const diff = this.revenueEndDate.diff(this.revenueStartDate, 'months').months;
    this.revenueGroupingMode = diff > 12 ? 'yearly' : 'monthly';
    this.revenueManualGroupingMode = false;
  }

  private _loadRevenueChart(): void {
    this.isLoadingRevenueChart = true;
    
    const range = this.revenueSelectedRange as any;
    const isYearSelection = !isNaN(Number(range));
    const isCustomRange = range === 'custom' || isYearSelection;
    
    const startDate = isCustomRange ? this.revenueStartDate.toJSDate() : undefined;
    const endDate = isCustomRange ? this.revenueEndDate.toJSDate() : undefined;
    const effectiveRange = isCustomRange ? 'custom' : range;
    
    const sub = this._apiService.getAdminRevenueChart(
      effectiveRange,
      this.revenueGroupingMode,
      startDate,
      endDate
    ).subscribe({
      next: (response: APIResponse) => {
        if (response.success) {
          this.revenueSummary = response.data.summary;
          this._buildRevenueChart(response.data.chartData);
        }
        this.isLoadingRevenueChart = false;
      },
      error: () => {
        this.isLoadingRevenueChart = false;
      }
    });
    this._subscriptions.push(sub);
  }

  private _buildRevenueChart(data: any[]): void {
    const categories = data.map(d => d.label);
    const revenueData = data.map(d => d.revenue / 100); // Konwersja z groszy na złote
    const ordersData = data.map(d => d.orderCount);

    this.revenueChartConfig = {
      series: [
        { name: 'Przychód (PLN)', type: 'column', data: revenueData },
        { name: 'Liczba zamówień', type: 'line', data: ordersData }
      ],
      chart: {
        type: 'line',
        height: 400,
        toolbar: { show: false },
        zoom: { enabled: false }
      },
      colors: ['#10B981', '#3B82F6'],
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: '60%',
          borderRadius: 6
        }
      },
      dataLabels: { enabled: false },
      stroke: {
        width: [0, 3],
        curve: 'smooth'
      },
      xaxis: {
        categories: categories,
        labels: {
          rotate: -45,
          rotateAlways: categories.length > 12
        }
      },
      yaxis: [
        {
          title: { text: 'Przychód (PLN)' },
          labels: {
            formatter: (val: number) => val.toFixed(2) + ' zł'
          }
        },
        {
          opposite: true,
          title: { text: 'Liczba zamówień' },
          labels: {
            formatter: (val: number) => Math.round(val).toString()
          }
        }
      ],
      tooltip: {
        y: {
          formatter: (val: number, { seriesIndex }: any) => {
            if (seriesIndex === 0) return val.toFixed(2) + ' zł';
            return Math.round(val).toString();
          }
        }
      },
      legend: {
        position: 'top',
        horizontalAlign: 'left'
      },
      grid: {
        borderColor: '#e7e7e7',
        strokeDashArray: 4
      },
      markers: {
        size: [0, 4],
        hover: { size: 6 }
      }
    };
  }

  formatCurrency(amountInCents: number): string {
    return (amountInCents / 100).toFixed(2);
  }

  getChangeIcon(change: number): string {
    if (change > 0) return 'tabler_outline:trending-up';
    if (change < 0) return 'tabler_outline:trending-down';
    return 'tabler_outline:minus';
  }

  getChangeClass(change: number): string {
    if (change > 0) return 'text-green-600 dark:text-green-400';
    if (change < 0) return 'text-red-600 dark:text-red-400';
    return 'text-gray-600 dark:text-gray-400';
  }

  formatSyncDate(dateStr: string): string {
    const dt = DateTime.fromISO(dateStr);
    if (!dt.isValid) return dateStr;
    const diff = DateTime.now().diff(dt, 'minutes').minutes;
    if (diff < 1) return 'przed chwilą';
    if (diff < 60) return `${Math.floor(diff)} min temu`;
    if (diff < 1440) return `${Math.floor(diff / 60)}h temu`;
    return dt.toFormat('dd.MM.yyyy HH:mm');
  }

  // ========== Register Source Stats ==========

  private static readonly SOURCE_CONFIG: Record<string, { label: string; icon: string; color: string }> = {
    'WEB': { label: 'Web', icon: 'tabler_outline:world', color: '#3B82F6' },
    'IOS': { label: 'iOS', icon: 'tabler_outline:brand-apple', color: '#6366F1' },
    'ANDROID': { label: 'Android', icon: 'tabler_outline:brand-android', color: '#10B981' },
    'MOBILE': { label: 'Mobile', icon: 'tabler_outline:device-mobile', color: '#F59E0B' },
    'UNKNOWN': { label: 'Nieznane', icon: 'tabler_outline:help', color: '#9CA3AF' }
  };

  getSourceConfig(source: string): { label: string; icon: string; color: string } {
    return AdminDashboardComponent.SOURCE_CONFIG[source] || AdminDashboardComponent.SOURCE_CONFIG['UNKNOWN'];
  }

  getSourcePercentage(count: number): number {
    if (!this.overviewStats || this.overviewStats.totalUsers === 0) return 0;
    return Math.round((count / this.overviewStats.totalUsers) * 1000) / 10;
  }

  backfillRegisterSource(): void {
    if (this.isBackfillingRegisterSource) return;
    this.isBackfillingRegisterSource = true;

    const sub = this._apiService.backfillRegisterSource().subscribe({
      next: (response: APIResponse) => {
        this.isBackfillingRegisterSource = false;
        if (response.success) {
          const data = response.data;
          this._snackbarService.showSnackBar(
            `Uzupełniono ${data.updated} z ${data.total} użytkowników. WEB: ${data.breakdown.WEB}, IOS: ${data.breakdown.IOS}, ANDROID: ${data.breakdown.ANDROID}, MOBILE: ${data.breakdown.MOBILE}, Nieznane: ${data.breakdown.UNKNOWN}`,
            'success',
            8000
          );
          // Odśwież statystyki
          this._loadOverviewStats();
        } else {
          this._snackbarService.showSnackBar('Nie udało się uzupełnić danych.', 'error');
        }
      },
      error: () => {
        this.isBackfillingRegisterSource = false;
        this._snackbarService.showSnackBar('Błąd podczas uzupełniania registerSource.', 'error');
      }
    });
    this._subscriptions.push(sub);
  }

  private _buildRegisterSourceChart(): void {
    if (!this.overviewStats?.registerSourceStats?.length) return;

    const stats = this.overviewStats.registerSourceStats;
    const labels = stats.map(s => this.getSourceConfig(s.source).label);
    const series = stats.map(s => s.count);
    const colors = stats.map(s => this.getSourceConfig(s.source).color);

    this.registerSourceChartConfig = {
      series: series,
      labels: labels,
      chart: {
        type: 'donut',
        height: 280
      },
      colors: colors,
      plotOptions: {
        pie: {
          donut: {
            size: '65%',
            labels: {
              show: true,
              name: { show: true },
              value: {
                show: true,
                formatter: (val: string) => val
              },
              total: {
                show: true,
                label: 'Razem',
                formatter: (w: any) => w.globals.seriesTotals.reduce((a: number, b: number) => a + b, 0).toString()
              }
            }
          }
        }
      },
      dataLabels: { enabled: false },
      legend: {
        position: 'bottom',
        horizontalAlign: 'center'
      },
      tooltip: {
        y: {
          formatter: (val: number) => `${val} użytkowników`
        }
      }
    };
  }

  // ========== Register Source Time Chart ==========

  private _loadRegisterSourceChart(): void {
    this.isLoadingRegisterSourceChart = true;

    const range = this.selectedRange as any;
    const isYearSelection = !isNaN(Number(range));
    const isCustomRange = range === 'custom' || isYearSelection;

    const startDate = isCustomRange ? this.startDate.toJSDate() : undefined;
    const endDate = isCustomRange ? this.endDate.toJSDate() : undefined;
    const effectiveRange = isCustomRange ? 'custom' : range;

    const sub = this._apiService.getAdminRegisterSourceChart(effectiveRange, startDate, endDate).subscribe({
      next: (response: APIResponse) => {
        if (response.success) {
          this._buildRegisterSourceTimeChart(response.data);
        }
        this.isLoadingRegisterSourceChart = false;
      },
      error: () => {
        this.isLoadingRegisterSourceChart = false;
      }
    });
    this._subscriptions.push(sub);
  }

  private _buildRegisterSourceTimeChart(data: any[]): void {
    const categories = data.map(d => d.month);

    const series = SOURCES_ORDER
      .filter(source => data.some(d => d[source] > 0))
      .map(source => ({
        name: this.getSourceConfig(source).label,
        data: data.map(d => d[source] || 0)
      }));

    const colors = SOURCES_ORDER
      .filter(source => data.some(d => d[source] > 0))
      .map(source => this.getSourceConfig(source).color);

    this.registerSourceTimeChartConfig = {
      series,
      chart: {
        type: 'bar',
        height: 400,
        stacked: true,
        toolbar: { show: false },
        zoom: { enabled: false }
      },
      colors,
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: '60%',
          borderRadius: 4
        }
      },
      dataLabels: { enabled: false },
      stroke: {
        show: true,
        width: 1,
        colors: ['transparent']
      },
      xaxis: {
        categories,
        labels: {
          rotate: -45,
          rotateAlways: categories.length > 12
        }
      },
      yaxis: {
        labels: {
          formatter: (val: number) => Math.round(val).toString()
        }
      },
      fill: { opacity: 1 },
      tooltip: {
        y: {
          formatter: (val: number) => `${Math.round(val)} rejestracji`
        }
      },
      legend: {
        position: 'top',
        horizontalAlign: 'left'
      },
      grid: {
        borderColor: '#e7e7e7',
        strokeDashArray: 4
      }
    };
  }
}
